import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";

const ZONE_COLORS = {
  Dry: [34, 197, 94], Elevated: [234, 179, 8], High: [249, 115, 22],
  Critical: [239, 68, 68], Saturated: [153, 27, 27],
};

export default function JobReportPDF({ job, detail }) {
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pw = 210;
      const margin = 15;
      const cw = pw - margin * 2;
      let y = 15;

      const addText = (text, size, bold, color) => {
        doc.setFontSize(size);
        doc.setFont("helvetica", bold ? "bold" : "normal");
        doc.setTextColor(...(color || [30, 30, 30]));
        const lines = doc.splitTextToSize(text, cw);
        if (y + lines.length * size * 0.4 > 280) { doc.addPage(); y = 15; }
        doc.text(lines, margin, y);
        y += lines.length * size * 0.4 + 2;
      };

      const addLine = () => {
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, y, pw - margin, y);
        y += 4;
      };

      // Header
      doc.setFillColor(2, 8, 17);
      doc.rect(0, 0, pw, 32, "F");
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text("APEX FIELDPROIQ", margin, 12);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(150, 150, 150);
      doc.text("WATER DAMAGE / JOB DOCUMENTATION REPORT", margin, 18);
      doc.setFontSize(10);
      doc.setTextColor(220, 38, 38);
      doc.text(job.request_code || "", pw - margin, 12, { align: "right" });
      doc.setTextColor(150, 150, 150);
      doc.setFontSize(8);
      doc.text(new Date().toLocaleDateString(), pw - margin, 18, { align: "right" });
      y = 38;

      // Job Info
      addText("JOB INFORMATION", 11, true, [220, 38, 38]);
      y += 1;
      const infoRows = [
        ["Service Type", job.service_type_label || "N/A"],
        ["Status", job.current_status?.toUpperCase() || "N/A"],
        ["Customer", job.customer_name || "N/A"],
        ["Property", `${job.property_address_line1 || ""}, ${job.property_city || ""} ${job.property_state || ""} ${job.property_zip_code || ""}`],
        ["Created", job.created_at ? new Date(job.created_at).toLocaleString() : "N/A"],
        ["Completed", job.completed_at ? new Date(job.completed_at).toLocaleString() : "N/A"],
      ];
      doc.setFontSize(8);
      infoRows.forEach(([label, value]) => {
        doc.setFont("helvetica", "bold");
        doc.setTextColor(100, 100, 100);
        doc.text(label + ":", margin, y);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(30, 30, 30);
        doc.text(value, margin + 30, y);
        y += 5;
      });
      y += 3;
      addLine();

      // Description
      if (detail?.problem_description || job.problem_description) {
        addText("DESCRIPTION", 11, true, [220, 38, 38]);
        addText(detail?.problem_description || job.problem_description, 9, false);
        y += 2;
        addLine();
      }

      // Moisture Readings
      if (detail?.moisture?.readings?.length > 0) {
        addText("MOISTURE READINGS", 11, true, [220, 38, 38]);
        y += 1;
        // Table header
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, y - 1, cw, 6, "F");
        doc.setFontSize(7);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(80, 80, 80);
        const cols = [margin, margin + 15, margin + 55, margin + 80, margin + 110, margin + 140];
        doc.text("Point", cols[0], y + 3);
        doc.text("Material", cols[1], y + 3);
        doc.text("Moisture %", cols[2], y + 3);
        doc.text("Zone", cols[3], y + 3);
        doc.text("Risk", cols[4], y + 3);
        doc.text("Action", cols[5], y + 3);
        y += 8;

        detail.moisture.readings.forEach(r => {
          if (y > 275) { doc.addPage(); y = 15; }
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7);
          doc.setTextColor(30, 30, 30);
          doc.text(r.pointLabel, cols[0], y);
          doc.text(r.material, cols[1], y);
          const zc = ZONE_COLORS[r.zone] || [100, 100, 100];
          doc.setTextColor(...zc);
          doc.setFont("helvetica", "bold");
          doc.text(`${r.pct}%`, cols[2], y);
          doc.text(r.zone, cols[3], y);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(100, 100, 100);
          doc.text(r.risk || "", cols[4], y);
          doc.text((r.recommendedAction || "").substring(0, 25), cols[5], y);
          y += 5;
        });
        y += 3;
        addLine();
      }

      // AI Analysis
      if (detail?.moisture?.ai_analysis) {
        const ai = detail.moisture.ai_analysis;
        addText("MOISTURE ANALYSIS", 11, true, [220, 38, 38]);
        y += 1;
        const aiRows = [
          ["Severity", ai.severity], ["Water Class", `Class ${ai.iicrcClass}`],
          ["Drying Estimate", ai.dryingDays], ["Mold Risk", ai.moldRisk],
        ];
        doc.setFontSize(8);
        aiRows.forEach(([l, v]) => {
          doc.setFont("helvetica", "bold"); doc.setTextColor(100, 100, 100);
          doc.text(l + ":", margin, y);
          doc.setFont("helvetica", "normal"); doc.setTextColor(30, 30, 30);
          doc.text(v || "N/A", margin + 35, y);
          y += 5;
        });
        y += 2;
        if (ai.summary) { addText(ai.summary, 8, false, [60, 60, 60]); }
        if (ai.immediateActions?.length) {
          y += 2;
          addText("Immediate Actions:", 8, true, [200, 50, 50]);
          ai.immediateActions.forEach((a, i) => addText(`${i + 1}. ${a}`, 8, false, [60, 60, 60]));
        }
        if (ai.insuranceNotes) {
          y += 2;
          addText("Insurance Notes:", 8, true, [34, 120, 60]);
          addText(ai.insuranceNotes, 8, false, [60, 60, 60]);
        }
        y += 3;
        addLine();
      }

      // Inventory
      if (detail?.inventory?.length > 0) {
        addText("INVENTORY & MATERIALS", 11, true, [220, 38, 38]);
        detail.inventory.forEach(item => {
          if (y > 275) { doc.addPage(); y = 15; }
          doc.setFontSize(8);
          doc.setFont("helvetica", "bold"); doc.setTextColor(30, 30, 30);
          doc.text(`${item.name} (${item.category})`, margin, y);
          doc.setFont("helvetica", "normal"); doc.setTextColor(100, 100, 100);
          doc.text(`Qty: ${item.quantity} ${item.unit} | $${item.total_cost?.toFixed(2)}${item.left_on_site ? " | LEFT ON SITE" : ""}`, margin + 80, y);
          y += 5;
        });
        y += 3;
        addLine();
      }

      // Expenses
      if (detail?.expenses?.length > 0) {
        addText("EXPENSES", 11, true, [220, 38, 38]);
        let expTotal = 0;
        detail.expenses.forEach(e => {
          if (y > 275) { doc.addPage(); y = 15; }
          doc.setFontSize(8);
          doc.setFont("helvetica", "normal"); doc.setTextColor(30, 30, 30);
          doc.text(`${e.category}: ${e.description || ""}`, margin, y);
          doc.text(`$${e.amount?.toFixed(2)}`, pw - margin, y, { align: "right" });
          expTotal += e.amount || 0;
          y += 5;
        });
        doc.setFont("helvetica", "bold");
        doc.text(`Total: $${expTotal.toFixed(2)}`, pw - margin, y, { align: "right" });
        y += 5;
        addLine();
      }

      // Checklist
      if (detail?.checklist?.items?.length > 0) {
        addText(`CHECKLIST (${detail.checklist.progress}% Complete)`, 11, true, [220, 38, 38]);
        detail.checklist.items.forEach(item => {
          if (y > 275) { doc.addPage(); y = 15; }
          doc.setFontSize(7);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(item.checked ? 34 : 150, item.checked ? 197 : 150, item.checked ? 94 : 150);
          doc.text(item.checked ? "[X]" : "[ ]", margin, y);
          doc.setTextColor(60, 60, 60);
          doc.text(item.label, margin + 8, y);
          if (item.required) {
            doc.setTextColor(220, 38, 38);
            doc.text("REQ", pw - margin, y, { align: "right" });
          }
          y += 4;
        });
        y += 3;
        addLine();
      }

      // Footer
      if (y > 260) { doc.addPage(); y = 15; }
      y = 270;
      addLine();
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      doc.text("Technician Signature: ________________________", margin, y);
      doc.text(`Date: ${new Date().toLocaleDateString()}`, pw - margin, y, { align: "right" });
      y += 6;
      doc.setFontSize(7);
      doc.text("Generated by APEX FieldProIQ", margin, y);

      // Save
      const filename = `${job.request_code || "report"}_${new Date().toISOString().split("T")[0]}.pdf`;
      doc.save(filename);
      toast.success(`Report exported: ${filename}`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate PDF");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Button onClick={generate} disabled={generating} size="sm" variant="outline"
      className="text-xs h-7 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
      data-testid="export-pdf-btn">
      {generating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <FileText className="w-3 h-3 mr-1" />}
      Export PDF
    </Button>
  );
}
